// ============================================================================
// Roof Designer — Import einer Gebäude-Dachfläche (RoofSurface → DesignerState)
// ----------------------------------------------------------------------------
// Pure, deterministische Konvertierung (kein DOM, kein Fetch — voll testbar):
//   - Geo-Polygon ({lat,lng}, RoofEditor/MapLibre) → lokale Meter (äquirektangular
//     um den Zentroid) → Canvas-Pixel mit exakt bekanntem Maßstab. Die Kalibrierung
//     wird daher direkt confirmed gesetzt (m/px ist aus Geodaten exakt).
//   - Ohne Polygon: Rechteck-Platzhalter aus areaM2 (Seitenverhältnis 4:3),
//     deutlich markiert als "Geometrie vereinfacht aus Flächendaten".
// Leitplanken unverändert: canApprove false, requiresHumanReview true — der
// importierte Entwurf ist NIE eine finale Modulbelegung und NIE freigegeben.
// ============================================================================
import { initialDesignerState } from "./state.js";
import type { DesignerState, PixelPt, ImportSource, BackgroundGeoRef } from "./types.js";

// Quelle wie von GET /projects/:id/geospatial geliefert (Building.roofSurfaces[]).
export interface RoofSurfaceRecord {
  id: string;
  buildingId: string;
  label: string;
  roofType: string;
  tiltDeg: number;
  azimuthDeg: number;
  areaM2: number;
  usableAreaM2?: number | null;
  isUsable?: boolean;
  // Prisma-Json: Frontend-Konvention {lat,lng} (PolygonSchema in buildings.service);
  // defensiv wird auch {latitude,longitude} akzeptiert (GeoPoint aus shared-types).
  polygon?: unknown;
}

// Ziel-Zeichenbereich im Canvas (Canvas ist container-groß, ≥ ~800×500 typisch).
const FIT_W_PX = 640;
const FIT_H_PX = 420;
const MARGIN_PX = 60;
// Meter pro Grad Breitengrad (WGS84-Näherung; für Dachgrößen << 1 km völlig ausreichend).
const M_PER_DEG_LAT = 111_320;

interface LatLngPt {
  lat: number;
  lng: number;
}

/** Toleranter Polygon-Parser: akzeptiert {lat,lng} und {latitude,longitude}. */
export function parseSurfacePolygon(raw: unknown): LatLngPt[] {
  if (!Array.isArray(raw)) return [];
  const pts: LatLngPt[] = [];
  for (const p of raw) {
    if (p == null || typeof p !== "object") continue;
    const o = p as { lat?: unknown; lng?: unknown; latitude?: unknown; longitude?: unknown };
    const lat = typeof o.lat === "number" ? o.lat : typeof o.latitude === "number" ? o.latitude : null;
    const lng = typeof o.lng === "number" ? o.lng : typeof o.longitude === "number" ? o.longitude : null;
    if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    pts.push({ lat, lng });
  }
  return pts;
}

/**
 * Geo-Polygon → Pixel-Polygon + exakter Maßstab.
 * Rückgabe null bei degenerierter Geometrie (Spannweite ~0) → Platzhalter-Pfad.
 */
function geoPolygonToPixels(
  geo: LatLngPt[],
): { polygon: PixelPt[]; metersPerPixel: number; geoRef: BackgroundGeoRef } | null {
  const lat0 = geo.reduce((s, p) => s + p.lat, 0) / geo.length;
  const lng0 = geo.reduce((s, p) => s + p.lng, 0) / geo.length;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);

  // Lokale Meter um den Zentroid; Canvas-y wächst nach unten, Breitengrad nach
  // Norden → y invertieren, damit Norden im Canvas oben liegt.
  const metric = geo.map((p) => ({
    xM: (p.lng - lng0) * mPerDegLng,
    yM: -(p.lat - lat0) * M_PER_DEG_LAT,
  }));

  const xs = metric.map((p) => p.xM);
  const ys = metric.map((p) => p.yM);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX;
  const spanY = Math.max(...ys) - minY;
  if (!(spanX > 0.5) && !(spanY > 0.5)) return null; // < 0,5 m Ausdehnung = degeneriert

  const metersPerPixel = Math.max(spanX / FIT_W_PX, spanY / FIT_H_PX, 0.001);
  const polygon = metric.map((p) => ({
    x: MARGIN_PX + (p.xM - minX) / metersPerPixel,
    y: MARGIN_PX + (p.yM - minY) / metersPerPixel,
  }));
  // Georeferenz für den Satelliten-Hintergrund: macht die Abbildung Canvas→WGS84
  // invertierbar (background.ts). Nur bei echtem Geo-Polygon verfügbar.
  const geoRef: BackgroundGeoRef = {
    source: "esri_world_imagery",
    lat0,
    lng0,
    minXM: minX,
    minYM: minY,
    metersPerPixel,
    marginPx: MARGIN_PX,
    enabled: true,
  };
  return { polygon, metersPerPixel, geoRef };
}

/** Rechteck-Platzhalter aus reiner Flächenangabe (Seitenverhältnis 4:3). */
function placeholderFromArea(areaM2: number): { polygon: PixelPt[]; metersPerPixel: number } {
  const safeArea = areaM2 > 0 ? areaM2 : 30; // defensiver Minimalwert
  const widthM = Math.sqrt((safeArea * 4) / 3);
  const heightM = safeArea / widthM;
  const metersPerPixel = Math.max(widthM / FIT_W_PX, heightM / FIT_H_PX, 0.001);
  const wPx = widthM / metersPerPixel;
  const hPx = heightM / metersPerPixel;
  return {
    polygon: [
      { x: MARGIN_PX, y: MARGIN_PX },
      { x: MARGIN_PX + wPx, y: MARGIN_PX },
      { x: MARGIN_PX + wPx, y: MARGIN_PX + hPx },
      { x: MARGIN_PX, y: MARGIN_PX + hPx },
    ],
    metersPerPixel,
  };
}

/**
 * Baut den kompletten DesignerState aus einer Gebäude-Dachfläche.
 * Modul-Spezifikation/Abstände bleiben Default — der Nutzer plant ab hier weiter
 * (Hindernisse, Belegung, Ränder). Ergebnis ist IMMER ein Entwurf.
 */
export function designerStateFromRoofSurface(
  surface: RoofSurfaceRecord,
  importedAt: string = new Date().toISOString(),
): DesignerState {
  const geo = parseSurfacePolygon(surface.polygon);
  const fromGeo = geo.length >= 3 ? geoPolygonToPixels(geo) : null;
  const geometrySimplified = fromGeo === null;
  const { polygon, metersPerPixel } = fromGeo ?? placeholderFromArea(surface.areaM2);

  const importSource: ImportSource = {
    type: "roof_surface",
    roofSurfaceId: surface.id,
    buildingId: surface.buildingId,
    label: surface.label,
    roofType: surface.roofType,
    importedAreaM2: surface.areaM2,
    importedTiltDeg: surface.tiltDeg,
    importedAzimuthDeg: surface.azimuthDeg,
    geometrySimplified,
    importedAt,
  };

  const base = initialDesignerState();
  return {
    ...base,
    polygon,
    polygonClosed: true,
    // Satelliten-Hintergrund nur bei echter Geometrie (sonst kein Geo-Bezug).
    backgroundGeoRef: fromGeo?.geoRef ?? null,
    calibration: {
      a: null,
      b: null,
      realLengthM: base.calibration.realLengthM,
      metersPerPixel,
      confirmed: true, // Maßstab ist aus den Quelldaten exakt bekannt
    },
    tiltDeg: surface.tiltDeg,
    azimuthDeg: surface.azimuthDeg,
    tiltConfirmed: true, // Werte stammen aus der erfassten Gebäude-Dachfläche
    activeTool: "select",
    importSource,
    canApprove: false,
    requiresHumanReview: true,
  };
}
